import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Megaphone, Plus, Send, Trash2, BarChart2, Pencil,
  Mail, Eye, TrendingUp, FileText, ChevronRight, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

/* ── Shared ── */
const ESTADO_COLOR: Record<string, string> = {
  borrador:   'bg-gray-100 text-gray-600',
  programada: 'bg-blue-100 text-blue-700',
  enviando:   'bg-yellow-100 text-yellow-700',
  enviada:    'bg-green-100 text-green-700',
  cancelada:  'bg-red-100 text-red-600',
};
const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador', programada: 'Programada', enviando: 'Enviando...', enviada: 'Enviada', cancelada: 'Cancelada',
};

function KpiCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; color: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-text-secondary">{label}</span>
        <div className={`p-2 rounded-lg ${color}`}><Icon size={16} /></div>
      </div>
      <p className="text-2xl font-bold text-text-primary">{value}</p>
      {sub && <p className="text-xs text-text-secondary mt-0.5">{sub}</p>}
    </div>
  );
}

/* ── Tab: Campañas ── */
function CampanasTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: kpis } = useQuery({
    queryKey: ['marketing-kpis'],
    queryFn: () => api.get('/api/marketing/campanas/kpis').then(r => r.data),
  });
  const { data: campanas = [], isLoading } = useQuery({
    queryKey: ['campanas'],
    queryFn: () => api.get('/api/marketing/campanas').then(r => r.data),
  });

  const enviarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/marketing/campanas/${id}/enviar`).then(r => r.data),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['campanas'] });
      qc.invalidateQueries({ queryKey: ['marketing-kpis'] });
      toast.success(`Enviando a ${data.destinatarios} destinatarios`);
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al enviar')),
  });
  const eliminarMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/marketing/campanas/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['campanas'] }); toast.success('Campaña eliminada'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar')),
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Megaphone}   label="Campañas"         value={kpis?.totalCampanas ?? 0}  color="bg-pink-50 text-pink-600" />
        <KpiCard icon={Mail}        label="Correos enviados" value={kpis?.totalEnviados ?? 0}   color="bg-blue-50 text-blue-600" />
        <KpiCard icon={Eye}         label="Aperturas"        value={kpis?.totalAbiertos ?? 0}   color="bg-purple-50 text-purple-600" />
        <KpiCard icon={TrendingUp}  label="Tasa de apertura" value={`${kpis?.tasaApertura ?? 0}%`} sub="promedio global" color="bg-green-50 text-green-600" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <h3 className="font-semibold text-text-primary">Campañas</h3>
          <button
            onClick={() => navigate('/marketing/campanas/nueva')}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} /> Nueva campaña
          </button>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-text-secondary text-sm">Cargando...</div>
        ) : campanas.length === 0 ? (
          <div className="py-16 text-center">
            <Megaphone size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="font-medium text-text-primary">Sin campañas creadas</p>
            <p className="text-sm text-text-secondary mt-1">Crea tu primera campaña de email marketing</p>
            <button
              onClick={() => navigate('/marketing/campanas/nueva')}
              className="mt-4 inline-flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <Plus size={14} /> Nueva campaña
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {campanas.map((c: any) => (
              <div key={c.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 group">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text-primary">{c.nombre}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ESTADO_COLOR[c.estado] || 'bg-gray-100 text-gray-600'}`}>
                      {ESTADO_LABEL[c.estado] || c.estado}
                    </span>
                    {c.plantilla && (
                      <span className="text-xs text-text-secondary flex items-center gap-1">
                        <FileText size={11} /> {c.plantilla.nombre}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 flex-wrap">
                    {c.estado === 'enviada' && (
                      <>
                        <span className="text-xs text-text-secondary"><Mail size={11} className="inline mr-1" />{c.totalEnviados} enviados</span>
                        <span className="text-xs text-text-secondary"><Eye size={11} className="inline mr-1" />{c.totalAbiertos} abiertos</span>
                        {c.totalEnviados > 0 && (
                          <span className="text-xs text-green-600 font-medium">
                            {Math.round((c.totalAbiertos / c.totalEnviados) * 100)}% apertura
                          </span>
                        )}
                      </>
                    )}
                    {c.fechaProgramada && c.estado === 'programada' && (
                      <span className="text-xs text-text-secondary flex items-center gap-1">
                        <Clock size={11} /> Programada: {formatDate(c.fechaProgramada)}
                      </span>
                    )}
                    <span className="text-xs text-text-secondary">{formatDate(c.createdAt)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {(c.estado === 'borrador' || c.estado === 'programada') && (
                    <button
                      onClick={() => enviarMutation.mutate(c.id)}
                      disabled={enviarMutation.isPending}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium hover:bg-primary-100 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Send size={11} /> Enviar ahora
                    </button>
                  )}
                  <Link
                    to={`/marketing/campanas/${c.id}`}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors"
                  >
                    <ChevronRight size={16} />
                  </Link>
                  {c.estado === 'borrador' && (
                    <button
                      onClick={() => { if (confirm('¿Eliminar esta campaña?')) eliminarMutation.mutate(c.id); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Tab: Plantillas ── */
function PlantillasTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: plantillas = [], isLoading } = useQuery({
    queryKey: ['plantillas-marketing'],
    queryFn: () => api.get('/api/marketing/plantillas').then(r => r.data),
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/marketing/plantillas/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['plantillas-marketing'] }); toast.success('Plantilla eliminada'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar')),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => navigate('/marketing/plantillas/nueva')}
          className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
        >
          <Plus size={14} /> Nueva plantilla
        </button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-text-secondary text-sm">Cargando...</div>
      ) : plantillas.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-xl border border-gray-100">
          <FileText size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="font-medium text-text-primary">Sin plantillas creadas</p>
          <p className="text-sm text-text-secondary mt-1">Diseña plantillas para reutilizarlas en tus campañas</p>
          <button
            onClick={() => navigate('/marketing/plantillas/nueva')}
            className="mt-4 inline-flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} /> Nueva plantilla
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plantillas.map((p: any) => (
            <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-sm transition-shadow group">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-pink-50 text-pink-600">
                  <FileText size={18} />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => navigate(`/marketing/plantillas/${p.id}`)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => { if (confirm('¿Eliminar esta plantilla?')) eliminarMutation.mutate(p.id); }}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <p className="font-semibold text-text-primary">{p.nombre}</p>
              {p.descripcion && <p className="text-xs text-text-secondary mt-1 line-clamp-2">{p.descripcion}</p>}
              <p className="text-xs text-text-secondary mt-1">Asunto: <span className="text-text-primary">{p.asunto}</span></p>
              <p className="text-xs text-text-secondary mt-3">{formatDate(p.createdAt)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Shell ── */
export function MarketingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const isPlantillas = location.pathname === '/marketing/plantillas';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Marketing</h2>
        <p className="text-sm text-text-secondary mt-0.5">Campañas de correo masivo</p>
      </div>

      <div className="flex gap-1 border-b border-gray-100">
        <button
          onClick={() => navigate('/marketing')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            !isPlantillas ? 'border-primary-600 text-primary-700' : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          <BarChart2 size={14} /> Campañas
        </button>
        <button
          onClick={() => navigate('/marketing/plantillas')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            isPlantillas ? 'border-primary-600 text-primary-700' : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          <FileText size={14} /> Plantillas
        </button>
      </div>

      {isPlantillas ? <PlantillasTab /> : <CampanasTab />}
    </div>
  );
}
